import {
  ApartmentOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import {activityPlaces, procedures} from '@mep/pae-commons';
import {Button, Divider, Popconfirm, Tooltip, Typography} from 'antd';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import BooleanPreview from '@/components/BooleanPreview';
import {renderUrl} from '@/components/CellRenders';
import DownloadFile from '@/components/DownloadFile';
import FullAddressPreview from '@/components/FullAddressPreview';
import Phones from '@/components/PhoneNumberField/PhonesListView';
import useCrud from '@/hooks/useCrud';
import ActivityPlaceLinkType from '@/modules/activityPlace/components/ActivityPlaceLinkType';
import ActivityPlaceTypes from '@/modules/activityPlace/components/ActivityPlaceType';
import {CACHE_KEY_LIST_ACTIVITY_PLACE} from '@/modules/activityPlace/contants/activityPlace';
import ActivityPlaceApiService from '@/modules/activityPlace/services/ActivityPlaceApiService';
import MapViewerViewWidget from '@/modules/company/components/OfficeMap/components/MapViewerViewWidget';
import MockLocationImg
  from '@/modules/company/containers/CompanyInfoDetailContainer/containers/OfficesPanelContainer/components/MockLocationImg';
import OfficeEmails
  from '@/modules/company/containers/CompanyInfoDetailContainer/containers/OfficesPanelContainer/components/OfficeEmails';
import {useGetOneProcedure} from '@/modules/company/hooks/useProcedure';
import ActivitiesListPreview
  from '@/modules/proposalRequest/components/ProposalActivitiesSelect/components/ActivitiesListPreview';
import VerifyListItem
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/VerifyListItem';
import {useAddressFull} from '@/modules/proposalRequest/hooks/useAddressFull';

import styles from '../../../index.less';

const {PROCEDURE_STATUS} = procedures;
const {ACTIVITY_PLACE_TYPES} = activityPlaces;

const {Text} = Typography;

const Office = ({
  emails,
  webSite,
  address,
  phones,
  primary,
  activities,
  allowRemove,
  softDelete,
  procedure,
  checkToRemove,
  tenant,
  ...rest
}) => {
  const {procedureId} = useParams();
  const {remove, update, isLoading} = useCrud(ActivityPlaceApiService, CACHE_KEY_LIST_ACTIVITY_PLACE);
  const {t} = useTranslation('activityPlace');
  const {data: procedureData} = useGetOneProcedure({id: procedure || procedureId}, {refetchOnWindowFocus: false});
  
  const coordinates = useMemo(() => (address?.location?.coordinates || [])
    ?.map(v => v)
    ?.filter(v => !!v),
  [address?.location?.coordinates]);
  
  const mainAddress = useAddressFull(address, false, '');
  
  const onRemove = useCallback(() => {
    if (!softDelete || (!!(procedure || procedureId) && procedureData?.data?.status === PROCEDURE_STATUS.DRAFT)) {
      return remove(rest._id);
    } else {
      return update({_id: rest?._id, checkToRemove: !checkToRemove});
    }
  }, [softDelete, procedure, procedureId, procedureData?.data?.status, remove, rest._id, update, checkToRemove]);
  
  return (
    <div className={classNames(styles.officeContainer, 'w-full relative')} {...rest}>
      <div
        className={classNames('flex items-start justify-center w-full relative gap-x-4 pb-4')}
      >
        {!!address && <div className="min-w-1/3 mx-auto pt-4 pl-4">
          {!isEmpty(coordinates) ?
            <MapViewerViewWidget showInputs={false} coordinates={coordinates}/> :
            <MockLocationImg/>
          }
        </div>}
        <div className={classNames('flex-1 pt-4 flex flex-col gap-y-1 mr-4', {'pl-4': !address})}>
          <div className='flex flex-1 items-center justify-between'>
            <div className='flex flex-col gap-y-1'>
              {!!rest?.type && rest?.type !== ACTIVITY_PLACE_TYPES.STANDARD &&
                <VerifyListItem labelKey={t('form.type')} removeSpace fullSpace={false}>
                  <ActivityPlaceTypes status={rest?.type} className="max-w-56"/>
                </VerifyListItem>}
              
              {!!mainAddress &&
                <VerifyListItem
                  dots={false}
                  forceRow
                  fullView
                  labelKey={<EnvironmentOutlined style={{color: '#7a7a7a'}} className="text-xl mr-1"/>}
                  removeSpace
                >
                  <div className='flex items-center justify-between'>
                    <Text className={classNames('font-normal')}>{mainAddress}</Text>
                  </div>
                </VerifyListItem>}
            </div>
            
            {(allowRemove) &&
              <div className="self-start ml-4">
                {(checkToRemove && softDelete) ?
                  <Tooltip parentContainer title={t('Cancelar eliminar')}>
                    <Button type="default" loading={isLoading} onClick={onRemove}>
                      {t('Cancelar eliminar')}
                    </Button>
                  </Tooltip> :
                  <Popconfirm
                    key={'remove'}
                    placement="bottomRight"
                    title={t('deleteConfirmation')}
                    onConfirm={onRemove}
                    okText={t('delete')}
                    cancelText={t('cancel')}
                  >
                    <Tooltip parentContainer title={t('delete')}>
                      <Button type="text" icon={<DeleteOutlined/>} loading={isLoading}/>
                    </Tooltip>
                  </Popconfirm>
                }
              </div>}
          </div>
          {!!rest?.linkType && <VerifyListItem labelKey={t('linkType.label')} removeSpace fullSpace={false}>
            <ActivityPlaceLinkType type={rest?.linkType}/>
          </VerifyListItem>}
          
          {!!rest?.registryCertificate && <VerifyListItem labelKey={t('documents')} fullView removeSpace>
            <DownloadFile value={rest?.registryCertificate} isFull/>
          </VerifyListItem>}
          
          {!isEmpty(phones) && <VerifyListItem
            dots={false}
            forceRow
            truncate
            fullView
            labelKey={<PhoneOutlined style={{color: '#7a7a7a'}} className="text-xl mr-2"/>}
            removeSpace
          >
            <Phones showType={false} value={phones} useLink={false}/>
          </VerifyListItem>}
          {!isEmpty(emails?.filter(e => !!e)) &&
            <VerifyListItem
              dots={false}
              forceRow
              truncate
              fullView
              labelKey={<MailOutlined style={{color: '#7a7a7a'}} className="text-xl mr-2"/>}
              removeSpace
            >
              <OfficeEmails value={emails}/>
            </VerifyListItem>}
          {!!webSite &&
            <VerifyListItem
              dots={false}
              forceRow
              truncate
              fullView
              labelKey={<GlobalOutlined style={{color: '#7a7a7a'}} className="text-xl mr-2"/>}
              value={renderUrl(webSite || '-')}
              removeSpace
            />}
          
          <VerifyListItem
            labelKey={t('common:description')}
            removeSpace fullSpace={false}
            value={rest?.description || '-'}
          />
          
          <Divider className="my-1"/>
          
          {rest?.isOnPeninsula9045 && <VerifyListItem
            labelKey={t('form.isOnPeninsula9045')}
            removeSpace fullSpace={false}
          >
            <BooleanPreview value={rest?.isOnPeninsula9045}/>
          </VerifyListItem>}
          
          {!!tenant && <>
            <VerifyListItem labelKey={t('origins.label')} removeSpace fullSpace={false} value={tenant?.fullName}/>
            {tenant.nit &&
              <VerifyListItem labelKey={t('nit')} removeSpace fullSpace={false} value={tenant.nit}/>}
            {tenant.idNumber &&
              <VerifyListItem labelKey={t('idNumber')} removeSpace fullSpace={false} value={tenant.idNumber}/>}
            <VerifyListItem labelKey={t('address')} removeSpace fullSpace={false}>
              <FullAddressPreview value={tenant?.address} className={'text-base'}/>
            </VerifyListItem>
          </>}
        </div>
      </div>
      {!!activities?.length && <>
        <Divider className="m-2"/>
        
        <div className='pl-4'>
          <VerifyListItem
            removeSpace={primary}
            labelKey={<div className="flex items-center gap-x-1 mb-1">
              <ApartmentOutlined style={{color: '#7a7a7a'}} className="text-xl mr-2"/>
              {t('activities')}
            </div>}
            column
            dots={false}
          >
            <ActivitiesListPreview
              value={activities?.map(a => a?._id || a)}
              onlyRead
              uppercase={false}
              hideHelp
            />
          </VerifyListItem>
        </div>
      </>}
    </div>
  );
};

export default Office;

Office.propTypes = {
  emails: PropTypes.array,
  webSite: PropTypes.any,
  address: PropTypes.object,
  tenant: PropTypes.object,
  phones: PropTypes.array,
  activities: PropTypes.array,
  phoneNumbers: PropTypes.any,
  procedure: PropTypes.string,
  primary: PropTypes.bool,
  checkToRemove: PropTypes.bool,
  softDelete: PropTypes.bool,
  id: PropTypes.any,
  onDeleteOffice: PropTypes.func,
  setEditModalVisible: PropTypes.func,
  office: PropTypes.any,
  allowRemove: PropTypes.bool,
};
